import type {
  AnswerValue,
  CheckOutcome,
  CheckStatus,
  DefectStatus,
  QuestionSeverity,
} from '@my/check-api';

// ────────────────────────────────────────────────────────────────────────────
// Presentation maps for recorded checks. Colours follow the driver app so a
// manager reading a record sees the same green / red / amber the driver
// tapped: Pass, Fail, N/A.
// ────────────────────────────────────────────────────────────────────────────

export const OUTCOME_BADGE: Record<CheckOutcome, string> = {
  Pass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Defect: 'border-red-200 bg-red-50 text-red-700',
  Advisory: 'border-amber-200 bg-amber-50 text-amber-800',
};

/** Left-edge stripe on a detail answer row and the grid's outcome dot. */
export const OUTCOME_DOT: Record<CheckOutcome, string> = {
  Pass: 'bg-emerald-500',
  Defect: 'bg-red-500',
  Advisory: 'bg-amber-400',
};

export const STATUS_BADGE: Record<CheckStatus, string> = {
  InProgress: 'border-blue-200 bg-blue-50 text-blue-700',
  Submitted: 'border-slate-300 bg-white text-slate-600',
};

/**
 * Answer pills. `Ok` renders as "Pass" and `Defect` as "Fail" — the wire enum
 * kept its original names, the driver-facing words did not.
 */
export const ANSWER_BADGE: Record<AnswerValue, string> = {
  Ok: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Defect: 'border-red-200 bg-red-50 text-red-700',
  NotApplicable: 'border-amber-200 bg-amber-50 text-amber-800',
};

export const SEVERITY_BADGE: Record<QuestionSeverity, string> = {
  Low: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Medium: 'border-amber-200 bg-amber-50 text-amber-800',
  High: 'border-red-200 bg-red-50 text-red-700',
};

export const DEFECT_STATUS_BADGE: Record<DefectStatus, string> = {
  Open: 'border-red-200 bg-white text-red-600',
  InProgress: 'border-blue-200 bg-blue-50 text-blue-600',
  Deferred: 'border-slate-300 bg-white text-slate-600',
  Resolved: 'border-slate-200 bg-slate-100 text-slate-500',
};

/** Section keys in the order the answers imply — same approach as templates. */
export function answerSections(
  answers: readonly { sectionKey: string; sortOrder: number }[],
): string[] {
  const firstSeen = new Map<string, number>();
  for (const a of answers) {
    const prev = firstSeen.get(a.sectionKey);
    if (prev === undefined || a.sortOrder < prev) firstSeen.set(a.sectionKey, a.sortOrder);
  }
  return [...firstSeen.entries()].sort((x, y) => x[1] - y[1]).map(([k]) => k);
}

export function sectionLabelFrom(
  answers: readonly { sectionKey: string; sectionLabel: string }[],
  sectionKey: string,
): string {
  return answers.find((a) => a.sectionKey === sectionKey)?.sectionLabel ?? sectionKey;
}

/** Local date + time, e.g. "07/09/2026, 07:03". */
export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : `${d.toLocaleDateString()}, ${d.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
}

/** Midnight local, as an ISO string — for the date-range presets. */
export function startOfDaysAgo(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString();
}
